import React, { useState } from 'react';
import { ENVIRONMENT_STYLES, TRANSFORMATION_STYLES } from '../../utils/constants';
import StyleThumbnail from '../shared/StyleThumbnail';
import dubaiPoliceLogo from '../../assets/dubai-police-logo.png';

const ITEMS_PER_PAGE = 9;

const StyleSelectionScreen = ({ capturedPhoto, onStyleSelect, onBack }) => {
  const generationMode = 'transformation';
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const STYLES = generationMode === 'environment' ? ENVIRONMENT_STYLES : TRANSFORMATION_STYLES;
  const styleKeys = Object.keys(STYLES);
  const totalPages = Math.ceil(styleKeys.length / ITEMS_PER_PAGE);
  const paginatedKeys = styleKeys.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);

  const handleContinue = () => {
    if (!selectedStyle) return;
    onStyleSelect({
      ...STYLES[selectedStyle],
      generationMode
    });
  };

  const handlePrevPage = () => {
    if (currentPage > 0) setCurrentPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) setCurrentPage((prev) => prev + 1);
  };

  return (
    <div className="h-screen relative overflow-hidden bg-gradient-to-b from-white via-[#F6FBF9] to-[#E4EFEA]">
      {/* Vertical spine */}
      <div className="forensic-spine" />

      {/* Background layers */}
      <div className="forensic-grid-overlay" />
      <div className="forensic-map-overlay opacity-[0.05]" />
      <div className="forensic-circuit-lines" />

      {/* Geometric blocks */}
      <div className="forensic-block forensic-block-2 opacity-50" />
      <div className="forensic-corner-tl" />

      {/* Main content */}
      <div className="relative z-10 h-full flex flex-col">
        {/* Compact Header */}
        <header className="flex justify-between items-center px-8 py-3 flex-shrink-0">
          <div className="pl-4">
            <h1 className="text-lg font-bold tracking-[0.1em] uppercase text-[#1A1A1A]">
              Select Your Style
            </h1>
            <p className="text-[10px] tracking-[0.15em] uppercase text-[#6B7770] font-arabic">
              اختر النمط الخاص بك
            </p>
          </div>
          <img src={dubaiPoliceLogo} alt="Dubai Police" className="h-10 w-auto" />
        </header>

        {/* Header line */}
        <div className="h-[2px] mx-8 bg-gradient-to-r from-[#0e734b] via-[#0e734b] to-transparent flex-shrink-0" style={{ backgroundSize: '40% 100%', backgroundRepeat: 'no-repeat' }} />

        {/* Captured Photo Preview - Above scenes */}
        <div className="flex justify-center px-8 py-3 flex-shrink-0">
          <div className="forensic-card p-3 flex items-center gap-4">
            <div className="h-[280px] aspect-[2/3] rounded-sharp overflow-hidden border border-[#0e734b]/30">
              <img
                src={capturedPhoto}
                alt="Captured"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.15em] text-[#0e734b] font-semibold">Your Photo</p>
              <p className="text-xs uppercase tracking-[0.1em] text-[#6B7770] font-arabic">صورتك</p>
            </div>
          </div>
        </div>

        {/* Controls - Above scenes */}
        <div className="flex-shrink-0 px-8 py-2">
          <div className="flex gap-3 justify-center">
            <button
              onClick={onBack}
              className="forensic-btn-secondary px-6 py-3 text-sm flex items-center gap-2"
            >
              <span>←</span>
              <span className="font-title uppercase">Back</span>
              <span className="opacity-60">|</span>
              <span className="font-arabic">رجوع</span>
            </button>
            <button
              onClick={handleContinue}
              disabled={!selectedStyle}
              className={`forensic-btn-primary px-8 py-3 text-base flex items-center gap-2 ${
                !selectedStyle ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {selectedStyle ? (
                <>
                  <span className="font-title uppercase">Generate</span>
                  <span className="text-white/60">|</span>
                  <span className="font-arabic font-bold">إنشاء</span>
                  <span>→</span>
                </>
              ) : (
                <>
                  <span className="font-title uppercase">Select</span>
                  <span className="text-white/60">|</span>
                  <span className="font-arabic font-bold">اختر</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Style Grid - Takes most of the space */}
        <div className="flex-1 px-8 py-3 min-h-0">
          <div className="forensic-card p-4 h-full flex flex-col">
            <div className="flex justify-between items-center mb-2 flex-shrink-0">
              <p className="text-xs uppercase tracking-[0.3em] text-[#0e734b] font-semibold">
                Available Roles
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-sm transition-all ${
                        i === currentPage ? 'bg-[#0e734b] w-8' : 'bg-[#D1DDD7] w-5'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="flex-1 flex items-center gap-3 min-h-0">
              {totalPages > 1 && (
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 0}
                  className={`w-12 h-12 rounded-sharp border-2 flex items-center justify-center text-2xl transition-all flex-shrink-0 ${
                    currentPage === 0
                      ? 'opacity-30 cursor-not-allowed border-[#D1DDD7]'
                      : 'border-[#0e734b] text-[#0e734b] hover:bg-[#0e734b]/10'
                  }`}
                >
                  ‹
                </button>
              )}

              <div className="grid grid-cols-3 gap-3 flex-1 h-full">
                {paginatedKeys.map((key) => (
                  <StyleThumbnail
                    key={key}
                    style={STYLES[key]}
                    isSelected={selectedStyle === key}
                    onSelect={() => setSelectedStyle(key)}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages - 1}
                  className={`w-12 h-12 rounded-sharp border-2 flex items-center justify-center text-2xl transition-all flex-shrink-0 ${
                    currentPage === totalPages - 1
                      ? 'opacity-30 cursor-not-allowed border-[#D1DDD7]'
                      : 'border-[#0e734b] text-[#0e734b] hover:bg-[#0e734b]/10'
                  }`}
                >
                  ›
                </button>
              )}
            </div>

            {/* Selection status */}
            <div className="flex items-center justify-center gap-2 text-[#6B7770] text-[10px] uppercase tracking-[0.2em] mt-2 flex-shrink-0">
              <div className="w-1.5 h-1.5 bg-[#0e734b] rounded-sm" />
              <span>{selectedStyle ? `${STYLES[selectedStyle].name} Selected` : 'Tap to Select a Style'}</span>
              <div className="w-1.5 h-1.5 bg-[#0e734b] rounded-sm" />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default StyleSelectionScreen;
